
import { LoadedEvent, LoadVideoOptions, VimeoEmbedParameters, VolumeChangeEvent, TimeRange } from "@vimeo/player";
import Player from "@vimeo/player";
import { catchError, from, lastValueFrom, map, of, Subject, take, timeout } from "rxjs";
import { VideoDebugLogger } from "../video-debug-logger";
import { OEmbedOptionsType, VideoPlayerOptions } from "../video-player-options";
import { isNotNullOrUndefined } from "@pcp/core/util";
import { TimeEvent } from "../video-player-events";

type PlayerEventType = 'seeking' | 'play' | 'pause' | 'seeked' | 'timeupdate' | 'ended';
type PlayerEvent = TimeEvent & { type: PlayerEventType };
type SeekingEvent = PlayerEventType & { type: 'seeking' };
type PlayEvent = PlayerEventType & { type: 'play' };
type PauseEvent = PlayerEventType & { type: 'pause' };
type SeekedEvent = PlayerEventType & { type: 'seeked' };
type TimeupdateEvent = PlayerEventType & { type: 'timeupdate' };
type EndedEvent = PlayerEventType & { type: 'ended' };
// type LoadedEvent = TimeEvent & { type: 'loaded' };
// type VolumeChangeEvent = VolumeChangeEvent & { type: 'volumechange' };


/**
 * Converts the VideoPlayerOptions for PCP
 * to a format compatible to vimeo.
 */
const convertToVimeoOptions = (options: VideoPlayerOptions): LoadVideoOptions => {
    const { forceAutoplay, forceMuted, ...vimeoOptions } = options;
    if (forceMuted) {
        vimeoOptions.muted = true;
    }

    return {
        ...options,
        // TODO: url param isnew, check how to handle it best
        url: `https://player.vimeo.com/video/${options.id}`,
    }
}

/** A video could not start with sound; see `VimeoPlayer.autoplayBlocked$`. */
export interface VideoAutoplayBlocked {
    /** Error the player reported, normally `NotAllowedError`. */
    errorName: string;
    /** Vimeo accepts both numeric ids and url-shaped ones, so this is not just a number. */
    vimeoId: undefined | number | string;
    /** Whether playing it muted afterwards succeeded. */
    retryMuted: boolean;
}

/** A video failed for a reason other than an autoplay refusal. */
export interface VideoPlaybackError {
    errorName: string;
    vimeoId: undefined | number | string;
}

/**
 * Wrapper around the Vimeo Player.
 * Add observable events.
 *
 * Could implement a common interface if we need support for multiple video sources (Vimeo, YouTube, native File etc.).
 */
export class VimeoPlayer {

    private player!: Player;
    private options!: VideoPlayerOptions;
    private readonly logger: VideoDebugLogger;

    private readonly playerEventSubject = new Subject<PlayerEvent>();
    readonly onPlayerEvent$ = this.playerEventSubject.asObservable();

    // private readonly onSeekingSubject = new Subject<TimeEvent>();
    // readonly seeking$ = this.onSeekingSubject.asObservable();

    // private readonly onPauseSubject = new Subject<TimeEvent>();
    // readonly pause$ = this.onPauseSubject.asObservable();

    // private readonly onPlaySubject = new Subject<TimeEvent>();
    // readonly play$ = this.onPlaySubject.asObservable();

    // private readonly onSeekedSubject = new Subject<TimeEvent>();
    // readonly seeked$ = this.onSeekedSubject.asObservable();

    private readonly onLoadedSubject = new Subject<LoadedEvent>();
    readonly loaded$ = this.onLoadedSubject.asObservable();

    // private readonly onTimeupdateSubject = new Subject<TimeEvent>();
    // readonly timeupdate$ = this.onTimeupdateSubject.asObservable();

    private readonly onVolumechangeSubject = new Subject<VolumeChangeEvent>();
    readonly volumechange$ = this.onVolumechangeSubject.asObservable();

    private readonly onAutoplayBlockedSubject = new Subject<VideoAutoplayBlocked>();
    /**
     * The browser refused to start this video with sound and it was retried muted.
     * Surfaced rather than logged here so the plain player stays free of DI; the
     * component turns it into telemetry.
     */
    readonly autoplayBlocked$ = this.onAutoplayBlockedSubject.asObservable();

    private readonly onPlaybackErrorSubject = new Subject<VideoPlaybackError>();
    /** The player reported an error that is not an autoplay refusal. */
    readonly playbackError$ = this.onPlaybackErrorSubject.asObservable();

    // private readonly onEndedSubject = new Subject<TimeEvent>();
    // readonly ended$ = this.onEndedSubject.asObservable();

    constructor(private readonly element: HTMLIFrameElement | HTMLElement | string) {
        this.logger = new VideoDebugLogger(this);
    }

    async init(options: VideoPlayerOptions, volume: undefined | number) {
        this.player = new Player(this.element, options as VimeoEmbedParameters);
        this.options = options;

        await this.player.ready();

        try {
            if (!options.forceMuted && volume !== undefined && volume >= 0 && volume <= 1) {
                await this.player.setVolume(volume);
            }
        } catch (error) {
            this.logger.error(`catched error during player init when setting volume to '${volume}'`, error);
        }

        if (options.forceAutoplay) {
            // IMPORTANT: `getPaused` check is required. Otherwise, the `play` promise will never resolve if the player is already playing.
            if (!await this.getIsPlayingSafe()) {
                await this.playFallbackToMuted();
            }
        } else if (options?.autoplay) {
            await this.player.pause();
        }

        this.listenOnAllEvents();
    }

    async loadNextVideo(options: VideoPlayerOptions, startPaused: boolean) {
        this.removeAllEvents();
        const videoIdHasChanged = this.videoIdHasChanged(options);
        this.options = options;

        if (videoIdHasChanged) {
            await this.player.unload();
            await this.player.loadVideo(convertToVimeoOptions(options));
        }
        await this.player.ready();
        if (options.autoplay && startPaused) {
            try {
                await this.pauseWithTimeout();
            } catch (error) {
                this.logger.warn(error);
            }
        } else {
            await this.playFallbackToMuted();
        }

        await this.player.ready();
        this.listenOnAllEvents();
    }

    async removeAllListenersAndDestroy() {
        this.removeAllEvents();
        await this.player.unload();
        await this.player.destroy();
    }

    videoIdHasChanged(newOptions: VideoPlayerOptions) {
        return newOptions.id && newOptions.id !== this.options.id;
    }
    rangeHasChanged(newOptions: VideoPlayerOptions) {
        return newOptions.start_time !== this.options.start_time
            || newOptions.end_time !== this.options.end_time;
    }
    static rangeHasValue(options: VideoPlayerOptions): boolean {
        return isNotNullOrUndefined(options.start_time) && options.start_time > 0 && Number.isFinite(options.start_time)
            || isNotNullOrUndefined(options.end_time) && options.end_time > 0 && Number.isFinite(options.end_time);
    }

    removeAllEvents() {
        this.player.off('play');
        this.player.off('pause');
        this.player.off('seeked');
        this.player.off('loaded');
        this.player.off('timeupdate');
        this.player.off('volumechange');
        this.player.off('ended');
        this.player.off('seeking');
    }

    listenOnAllEvents() {
        this.removeAllEvents();
        this.listenOnPauseEvent();
        this.listenOnPlayEvent();
        this.listenOnSeekedEvent();
        this.listenOnTimeupdateEvent();
        this.listenOnVolumechangeEvent();
        this.listenOnEndedEvent();
        this.listenOnSeekingEvent();

        // TODO: seems like the loaded event is never needed
        // this.listenOnLoadedEvent();
    }

    destroy = () => this.player.destroy();
    getBuffered = (): Promise<TimeRange[]> => this.player.getBuffered();
    getCurrentTime = () => this.player.getCurrentTime();

    getDuration = () => this.player.getDuration();
    getEnded = () => this.player.getEnded();
    getMuted = () => this.player.getMuted();
    getPaused = () => this.player.getPaused();
    getSeekable = () => this.player.getSeekable();

    getSeeking = () => this.player.getSeeking();
    getVideoId = () => this.player.getVideoId();
    getVolume = () => this.player.getVolume();

    getSeekingWithTimeout = async (t = 5000) => await lastValueFrom(from(this.player.getSeeking()).pipe(
        take(1),
        timeout(t),
        catchError(error => {
            this.logger.warn(`failed to getSeeking after ${t}ms`, error);
            return of(false);
        })
    ));

    getPausedWithTimeout = async (t = 5000) => await lastValueFrom(
        from(this.getIsPlayingSafe()).pipe(
            map(isPlaying => !isPlaying),
            take(1),
            timeout(t),
            catchError(error => {
                this.logger.warn(`failed to getPaused after ${t}ms`, error);
                return of(false);
            })
        )
    );

    getIsPlayingSafe = async () => {
        await this.player.ready()
        return await this.player.getCurrentTime() > 0
            && await this.player.getPaused() === false
            && await this.player.getEnded() === false;
    };

    /**
     * play/resume the video.
     * Automatically retries and mutes the player
     * in case a `NotAllowedError` is thrown.
     */
    play = async () => await this.playFallbackToMuted();

    private playFallbackToMuted = async () => {
        try {
            await this.player.ready();

            // To prevent triggering play when already playing:
            // `play` promise would not resolve otherwise.
            if (!await this.getIsPlayingSafe()) {
                await this.player.play();
            }
        } catch (error) {
            if ((error as Error).name === 'NotAllowedError') {
                // Auto play is possibly blocked.
                // Try again muted - muted autoplay is allowed in most browsers
                // https://developer.mozilla.org/en-US/docs/Web/API/HTMLMediaElement/play#exceptions
                // https://developer.mozilla.org/en-US/docs/Web/Media/Autoplay_guide#autoplay_and_autoplay_blocking
                this.logger.warn('could not play video. Trying muted...', error);

                let retryMuted = false;
                try {
                    await this.player.setMuted(true);
                    await this.player.play();
                    retryMuted = true;
                } finally {
                    // Reported whether or not the muted retry worked: a silent video
                    // and a dead one are different problems and both were invisible
                    // before, because this path only ever wrote to the console.
                    this.onAutoplayBlockedSubject.next({
                        errorName: (error as Error).name,
                        vimeoId: this.options?.id,
                        retryMuted,
                    });
                }
            } else if ((error as Error).name === 'PlayInterrupted') {
                this.logger.warn(error);
            } else {
                this.logger.error(error);
                this.onPlaybackErrorSubject.next({
                    errorName: (error as Error).name,
                    vimeoId: this.options?.id,
                });
                throw error;
            }
        }
    };

    pause = () => this.player.pause();
    ready = async () => await this.player.ready();
    setMuted = async (muted: boolean) => {
        try {
            await this.player.setMuted(muted);
        } catch (error) {
            this.logger.error(error);
        }
    };
    setVolume = async (volume: number) => {
        try {
            return await this.player.setVolume(volume);
        } catch (error) {
            this.logger.warn(error);
            return;
        }
    };
    unload = () => this.player.unload();

    /**
     * pause the video without listening on the follow-up pause event.
     */
    pauseNoEmit = async () => {
        this.player.off('pause');
        await this.player.pause();
        this.listenOnPauseEvent();
    };

    playNoEmit = async (atPosition?: number) => {
        this.player.off('play');
        this.player.off('pause');
        this.player.off('seeked');
        try {
            // TODO: does not complete after holding+releasing the time bar on the receiver side
            await this.playFallbackToMuted();

            // Synchronize the player position on play event.
            // NOTE: disable synchronization if event are getting emitted multiple times
            if (atPosition !== undefined && atPosition >= 0) {
                await this.player.setCurrentTime(atPosition);
            }
        } catch (error) {
            this.logger.warn(error);
        }
        this.listenOnPlayEvent();
        this.listenOnPauseEvent();
        this.listenOnSeekedEvent();
    };

    /**
     * `setCurrentTime` without emitting events.
     */
    async setCurrentTime(targetPosition: number) {
        this.player.off('play');
        this.player.off('pause');
        this.player.off('seeked');
        await this.player.setCurrentTime(targetPosition);
        this.listenOnPlayEvent();
        this.listenOnPauseEvent();
        this.listenOnSeekedEvent();
    }

    /**
     * Sets the current time of the player (seekTo).
     * When seeking to a target position, the player can emit the events
     * - pause
     * - seeked
     * - play
     *
     * @param targetPosition
     * @param paused
     * @param positionDeltaIgnore Ignore the event if the difference between the currentTime and the target position
     *                            is lower than the delta.
     */
    async seekTo(targetPosition: number, paused?: boolean, positionDeltaIgnore = 0.1) {
        const currentTime = await this.player.getCurrentTime();
        if (Math.abs(currentTime - targetPosition) < positionDeltaIgnore) {
            this.logger.debug('ignore seekTo as the currentTo is identical');
            return;
        }

        await this.player.setCurrentTime(targetPosition);
        const playerPausedState = await this.player.getPaused();

        // Task finished if the player is already in the corret paused/plaing state
        if (playerPausedState === paused) {
            return;
        }

        // TODO: synchronizing the "isPlaying" state after a seek seems to cause more problems than benefits.
        //       Commented out for now. Should be removed if really not needed.
        // const isPlaying = await this.getIsPlayingSafe();
        // if (paused === true && isPlaying) {
        //     await this.player.pause();
        // } else if (paused === false && !isPlaying) {
        //     await this.playWithTimeout();
        // }

        this.logger.log('finished seekTo', targetPosition);
    }

    /**
     * Sets the current time of the player (seekTo) without emitting en event.
     * Sets the player in the paused/playing state afterwards (if not already set).
     *
     * @param targetPosition
     * @param paused
     */
    async seekToNoEmit(targetPosition: number, paused?: boolean) {
        this.player.off('play');
        this.player.off('pause');
        this.player.off('seeked');
        await lastValueFrom(from(this.seekTo(targetPosition, paused)).pipe(
            // NOTE: don't set the timeout value too low to give the beamer enough time for buffering
            timeout(6000),
            catchError(error => {
                this.logger.warn(error);
                return of(false);
            }),
        ));

        this.listenOnPlayEvent();
        this.listenOnPauseEvent();
        this.listenOnSeekedEvent();
    }


    private playWithTimeout = async (t = 500) => await lastValueFrom(from(this.playFallbackToMuted()).pipe(
        take(1),
        timeout(t),
        catchError(error => {
            this.logger.warn(`player did not send a play signal after ${t}ms`, error);
            return of(false);
        })
    ));

    private pauseWithTimeout = async (t = 500) => await lastValueFrom(from(this.player.pause()).pipe(
        take(1),
        timeout(t),
        catchError(error => {
            this.logger.warn(`player did not send a pause signal after ${t}ms`, error);
            return of(false);
        })
    ));

    private listenOnPauseEvent = () => {
        this.player.off('pause')
        return this.player.on('pause', event => this.playerEventSubject.next({ ...event, type: 'pause' }));
    };
    private listenOnPlayEvent = () => {
        this.player.off('play')
        return this.player.on('play', event => this.playerEventSubject.next({ ...event, type: 'play' }));
    };
    private listenOnSeekedEvent = () => {
        this.player.off('seeked')
        return this.player.on('seeked', event => this.playerEventSubject.next({ ...event, type: 'seeked' }));
    };

    private listenOnLoadedEvent = () => {
        this.player.off('loaded')
        return this.player.on('loaded', event => this.onLoadedSubject.next(event));
    };
    private listenOnTimeupdateEvent = () => {
        this.player.off('timeupdate')
        return this.player.on('timeupdate', event => this.playerEventSubject.next({ ...event, type: 'timeupdate' }));
    };
    private listenOnVolumechangeEvent = () => {
        this.player.off('volumechange');
        return this.player.on('volumechange', async event => {
            const volume = await this.player.getMuted() ? { volume: 0, muted: true } : event;
            this.onVolumechangeSubject.next(volume);
        });
    };
    private listenOnEndedEvent = () => {
        this.player.off('ended')
        return this.player.on('ended', event => this.playerEventSubject.next({ ...event, type: 'ended' }));
    };
    private listenOnSeekingEvent = () => {
        this.player.off('seeking')
        return this.player.on('seeking', event => this.playerEventSubject.next({ ...event, type: 'seeking' }));
    };
}
